import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { EyeIcon, EyeSlashIcon, CheckIcon } from '@heroicons/react/24/outline';

const RegisterInfoPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { register, getDashboardUrl } = useAuth();

  const verifiedEmail = localStorage.getItem('verified_email');
  const verifiedPassword = localStorage.getItem('verified_password');
  const verifiedUserType = localStorage.getItem('verified_user_type');
  const defaultUserType = searchParams.get('type') || 'job_seeker';

  // 都道府県リスト
  const prefectures = [
    '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
    '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
    '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
    '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
    '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
    '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
    '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'
  ];

  // フォームデータ（メールとパスワードは認証済みのものを使用）
  const [formData, setFormData] = useState({
    email: verifiedEmail || '',
    password: verifiedPassword || '',
    confirmPassword: verifiedPassword || '',
    name: '',
    user_type: verifiedUserType || defaultUserType,
    phone: '',
    location: '',
    address: '',
    prefecture: '',
    city: '',
    bio: '',
    self_introduction: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!verifiedEmail) {
      toast.error('メール認証が必要です');
      navigate('/register');
    }
  }, [verifiedEmail, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // エラーをクリア
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // バリデーション関数
  const validateForm = () => {
    const newErrors = {};

    // 共通バリデーション - 名前（1文字以上）
    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = '名前は必須です';
    }

    // 電話番号（必須）
    if (!formData.phone || formData.phone.trim().length === 0) {
      newErrors.phone = '電話番号は必須です';
    } else if (!/^[0-9\-\+\(\)\s]+$/.test(formData.phone)) {
      newErrors.phone = '有効な電話番号を入力してください';
    }

    // メールアドレス（有効な形式）
    if (!formData.email || formData.email.trim().length === 0) {
      newErrors.email = 'メールアドレスは必須です';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = '有効なメールアドレスを入力してください';
    }

    // パスワード（6文字以上）
    if (!formData.password || formData.password.length < 6) {
      newErrors.password = 'パスワードは6文字以上で入力してください';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'パスワードが一致しません';
    }

    // 所在地/希望勤務地（都道府県と市区町村）- 共通で必須
    if (!formData.prefecture || formData.prefecture.trim().length === 0) {
      newErrors.prefecture = '都道府県は必須です';
    }
    
    if (!formData.city || formData.city.trim().length === 0) {
      newErrors.city = '市区町村は必須です';
    }

    // 求職者用バリデーション
    if (formData.user_type === 'job_seeker') {
      // 自己紹介（必須）
      if (!formData.self_introduction || formData.self_introduction.trim().length === 0) {
        newErrors.self_introduction = '自己紹介文は必須です';
      } else if (formData.self_introduction.trim().length < 20) {
        newErrors.self_introduction = '自己紹介文は20文字以上で入力してください';
      }
    }

    // 事業者用バリデーション
    if (formData.user_type === 'employer') {
      // 事業紹介（どんな文字列でもOK、ただし入力必須）
      if (!formData.bio || formData.bio.trim().length === 0) {
        newErrors.bio = '事業紹介は必須です';
      }
      
      // 詳細住所を必須に変更
      if (!formData.address || formData.address.trim().length === 0) {
        newErrors.address = '詳細住所は必須です';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // バリデーション実行
    if (!validateForm()) {
      toast.error('入力内容に誤りがあります。確認してください。');
      return;
    }

    setLoading(true);

    try {
      const userData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        user_type: formData.user_type,
        phone: formData.phone,
        prefecture: formData.prefecture || null,
        city: formData.city || null,
        location: formData.location || `${formData.prefecture} ${formData.city}`.trim(),
        bio: formData.bio || null,
        self_introduction: formData.self_introduction || null
      };

      console.log('Attempting registration with data:', userData);
      const result = await register(userData);
      console.log('Registration result:', result);
      
      if (result.success) {
        console.log('Registration successful, user type:', result.user.user_type);
        toast.success('アカウントを作成しました！ログインが完了しました。');
        
        // 認証情報をlocalStorageからクリア
        localStorage.removeItem('verified_email');
        localStorage.removeItem('verified_password');
        localStorage.removeItem('verified_user_type');
        
        // 状態更新を確実にするため少し待機
        setTimeout(() => {
          // ユーザータイプに基づいて適切なダッシュボードに遷移
          const dashboardUrl = getDashboardUrl(result.user.user_type);
          console.log(`Navigating to ${dashboardUrl} for ${result.user.user_type}`);
          navigate(dashboardUrl, { replace: true });
        }, 100);
      } else {
        console.error('Registration failed:', result.error);
        toast.error(result.error || '登録に失敗しました');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('登録に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  if (!verifiedEmail) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900">byters</h2>
          <p className="mt-2 text-sm text-gray-600">即日バイトマッチング</p>
          
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center justify-center">
              <CheckIcon className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-green-800 font-medium">
                メール認証完了: {verifiedEmail}
              </span>
            </div>
          </div>
          
          <h3 className="mt-6 text-2xl font-extrabold text-gray-900">
            アカウント情報入力
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            認証済みのメールアドレスでアカウントを作成します
          </p>
        </div>

        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-6 sm:p-8">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* User Type Selection */}
              <div>
                <label className="form-label">
                  アカウントタイプ
                </label>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <label 
                    className="relative cursor-pointer"
                    onClick={() => handleChange({ target: { name: 'user_type', value: 'job_seeker' } })}
                  >
                    <input
                      type="radio"
                      name="user_type"
                      value="job_seeker"
                      checked={formData.user_type === 'job_seeker'}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                      formData.user_type === 'job_seeker'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <div className="text-center">
                        <div className="text-2xl mb-2"></div>
                        <div className="font-medium">求職者</div>
                        <div className="text-sm text-gray-500">バイトを探す</div>
                      </div>
                    </div>
                  </label>
                  <label 
                    className="relative cursor-pointer"
                    onClick={() => handleChange({ target: { name: 'user_type', value: 'employer' } })}
                  >
                    <input
                      type="radio"
                      name="user_type"
                      value="employer"
                      checked={formData.user_type === 'employer'}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                      formData.user_type === 'employer'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <div className="text-center">
                        <div className="text-2xl mb-2"></div>
                        <div className="font-medium">事業者</div>
                        <div className="text-sm text-gray-500">人材を募集する</div>
                      </div>
                    </div>
                  </label>
                </div>

              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="form-label">
                    {formData.user_type === 'employer' ? '会社名・店舗名' : '氏名'} *
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className={`form-input ${errors.name ? 'border-red-500' : ''}`}
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={formData.user_type === 'employer' ? '株式会社サンプル' : '山田太郎'}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="form-label">
                    メールアドレス *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className={`form-input ${errors.email ? 'border-red-500' : ''}`}
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@example.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="password" className="form-label">
                    パスワード *
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      className={`form-input pr-10 ${errors.password ? 'border-red-500' : ''}`}
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="6文字以上"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="form-label">
                    パスワード確認 *
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      className={`form-input pr-10 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="パスワードを再入力"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>

              {/* 必須項目: 住所（都道府県と市区町村） */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="prefecture" className="form-label">
                    {formData.user_type === 'employer' ? '事業所所在地（都道府県）' : '希望勤務地（都道府県）'} *
                  </label>
                  <select
                    id="prefecture"
                    name="prefecture"
                    className={`form-input ${errors.prefecture ? 'border-red-500' : ''}`}
                    value={formData.prefecture}
                    onChange={handleChange}
                    required
                  >
                    <option value="">都道府県を選択してください</option>
                    {prefectures.map((pref) => (
                      <option key={pref} value={pref}>{pref}</option>
                    ))}
                  </select>
                  {errors.prefecture && (
                    <p className="mt-1 text-sm text-red-600">{errors.prefecture}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="city" className="form-label">
                    {formData.user_type === 'employer' ? '市区町村' : '希望勤務地（市区町村）'} *
                  </label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    className={`form-input ${errors.city ? 'border-red-500' : ''}`}
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="渋谷区"
                    required
                  />
                  {errors.city && (
                    <p className="mt-1 text-sm text-red-600">{errors.city}</p>
                  )}
                </div>
              </div>

              {/* 求職者用: 自己紹介（20文字以上必須） */}
              {formData.user_type === 'job_seeker' && (
                <div>
                  <label htmlFor="self_introduction" className="form-label">
                    自己紹介 * <span className="text-sm text-gray-500">（20文字以上）</span>
                  </label>
                  <textarea
                    id="self_introduction"
                    name="self_introduction"
                    rows={4}
                    className={`form-input ${errors.self_introduction ? 'border-red-500' : ''}`}
                    value={formData.self_introduction}
                    onChange={handleChange}
                    placeholder="スキルや経験、働く意気込みなどを20文字以上でご記入ください。"
                    required
                  />
                  <div className="mt-1 flex justify-between">
                    {errors.self_introduction && (
                      <p className="text-sm text-red-600">{errors.self_introduction}</p>
                    )}
                    <p className="text-sm text-gray-500">
                      {formData.self_introduction.length}/20文字
                    </p>
                  </div>
                </div>
              )}

              {/* 事業者用: 会社・店舗の紹介文（20文字以上必須） */}
              {formData.user_type === 'employer' && (
                <div>
                  <label htmlFor="bio" className="form-label">
                    会社・店舗の紹介 * <span className="text-sm text-gray-500">（20文字以上）</span>
                  </label>
                  <textarea
                    id="bio"
                    name="bio"
                    rows={3}
                    className={`form-input ${errors.bio ? 'border-red-500' : ''}`}
                    value={formData.bio}
                    onChange={handleChange}
                    placeholder="会社や店舗についてご紹介ください。事業内容、雰囲気、求める人材などを記載してください。"
                    required
                  />
                  <div className="mt-1 flex justify-between">
                    {errors.bio && (
                      <p className="text-sm text-red-600">{errors.bio}</p>
                    )}
                    <p className="text-sm text-gray-500">
                      {formData.bio.length}文字
                    </p>
                  </div>
                </div>
              )}

              {/* 事業者用: 詳細住所（必須） */}
              {formData.user_type === 'employer' && (
                <div>
                  <label htmlFor="address" className="form-label">
                    詳細住所 * <span className="text-sm text-gray-500">（事業所の番地・建物名）</span>
                  </label>
                  <input
                    id="address"
                    name="address"
                    type="text"
                    className={`form-input ${errors.address ? 'border-red-500' : ''}`}
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="1-2-3 渋谷ビル 4F"
                    required
                  />
                  {errors.address && (
                    <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                  )}
                </div>
              )}

              {/* 任意項目: その他の所在地情報 */}
              <div>
                <label htmlFor="location" className="form-label">
                  その他の所在地情報 <span className="text-sm text-gray-500">（任意）</span>
                </label>
                <input
                  id="location"
                  name="location"
                  type="text"
                  className="form-input"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="最寄駅からの距離、目印など"
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="form-button disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      作成中...
                    </div>
                  ) : (
                    'アカウント作成完了'
                  )}
                </button>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  すでにアカウントをお持ちですか？{' '}
                  <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                    ログイン
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterInfoPage;